import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FunctionsUsingCSI, NgTerminal, NgTerminalModule } from "ng-terminal";
import { lastValueFrom } from 'rxjs';
import { Readline } from 'xterm-readline';
import { AiService } from '../../services/ai/ai.service';
import { ApiService } from '../../services/api.service';
import { FileSystemService } from '../../services/file-system.service';
import { Mutex } from '../../services/mutex.service';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroCommandLine } from '@ng-icons/heroicons/outline';


@Component({
  selector: 'shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.css'],
  standalone: true,
  imports: [NgTerminalModule, NgIcon],
  providers: [
    provideIcons({ heroCommandLine })
  ],
})
export class ShellComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly prompt = '\n' + FunctionsUsingCSI.cursorColumn(1) + '$ ';
  terminalInput: string = '';
  rl = new Readline();
  constructor(private api: ApiService, private aiService: AiService, private mutex: Mutex, private fs: FileSystemService) {
    this.aiService.aiIsWriting$.subscribe((isWriting) => {
      this.disableStdin = isWriting ?? false;
      if (this.terminal) {
        this.terminal.underlying!.options.disableStdin = this.disableStdin;
      }
    });
  }

  @ViewChild('term', { static: false }) terminal!: NgTerminal;
  disableStdin: boolean = false;


  ngOnInit() {
    this.aiService.aiWritingChunk$.subscribe(async (chunk) => {
      if (chunk.section === 'shell') {
        if (chunk.chunk.startsWith('\n')) {
          this.rl.write(chunk.chunk.substring(1));
        } else {
          this.rl.write(chunk.chunk);
        }
      }
      if (chunk.fullContent && chunk.section === 'shell') {
        const release = await this.mutex.acquire("shell");
        this.rl.write('\n');
        await this.runCommand(chunk.fullContent);
        release();
      }
    });
  }

  async runTerminal() {
    while (true) {
      // Await user input
      const text = await this.rl.read('$ ');

      // Process the input
      await this.processCommand(text, false);
    }
  }

  async runCommand(command: string) {
    await this.processCommand(command.trim(), true);
  }

  ngAfterViewInit() {
    // Light theme configuration
    const lightTheme = {
      background: '#fafafa',
      foreground: '#1d1d1f',
      cursor: '#1594fd',
      cursorAccent: '#fafafa',
      selectionBackground: 'rgba(21, 148, 253, 0.3)',
      selectionForeground: '#1d1d1f',
      black: '#1d1d1f',
      red: '#ff3b30',
      green: '#34c759',
      yellow: '#ff9500',
      blue: '#007aff',
      magenta: '#af52de',
      cyan: '#5ac8fa',
      white: '#f5f5f7',
      brightBlack: '#86868b',
      brightRed: '#ff6961',
      brightGreen: '#4cd964',
      brightYellow: '#ffcc00',
      brightBlue: '#5ac8fa',
      brightMagenta: '#bf5af2',
      brightCyan: '#70d7ff',
      brightWhite: '#ffffff'
    };

    // Set light theme options FIRST before loading any addons
    if (this.terminal && this.terminal.underlying) {
      // Apply all terminal options
      this.terminal.underlying.options.theme = lightTheme;
      this.terminal.underlying.options.cursorBlink = true;
      this.terminal.underlying.options.fontFamily = "'SF Mono', 'Fira Code', 'Consolas', 'Monaco', monospace";
      this.terminal.underlying.options.fontSize = 13;
      this.terminal.underlying.options.lineHeight = 1;
      this.terminal.underlying.options.cursorStyle = 'bar';

      // Force refresh the terminal to apply theme
      this.terminal.underlying.refresh(0, this.terminal.underlying.rows - 1);
    }

    this.terminal.underlying?.loadAddon(this.rl);
    this.rl.setCheckHandler((text) => {
      const trimmedText = text.trimEnd();
      if (trimmedText.endsWith('&&')) {
        return false;
      }
      return true;
    });

    if (this.terminal) {
      // Print the banner on its own line; the prompt is printed by
      // runTerminal()'s rl.read('$ '), so don't write it a second time here
      // (doing so left the input cursor on a blank line below the prompt).
      this.rl.write('Welcome to the terminal!\r\n');
    }

    // Apply theme again after a short delay to ensure it takes effect
    setTimeout(() => {
      if (this.terminal && this.terminal.underlying) {
        this.terminal.underlying.options.theme = lightTheme;
        this.terminal.underlying.refresh(0, this.terminal.underlying.rows - 1);
      }
    }, 100);

    // Start the read loop only once ng-terminal has fitted the terminal to its
    // container. If the first rl.read('$ ') runs while term.cols is still at its
    // tiny pre-fit value, xterm-readline computes the 2-char prompt as wrapping
    // and emits a spurious blank line before the first command's output.
    this.startTerminalWhenSized();
  }

  private startTerminalWhenSized(attempt = 0): void {
    const cols = this.terminal?.underlying?.cols ?? 0;
    // cols > 2 means the prompt no longer wraps; cap attempts (~1s) as a
    // safety net so the terminal always starts even if sizing never reports.
    if (cols > 2 || attempt >= 50) {
      this.runTerminal();
      return;
    }
    setTimeout(() => this.startTerminalWhenSized(attempt + 1), 20);
  }

  ngOnDestroy(): void {
    // Clean up when the component is destroyed
    if (this.terminal) {
      this.terminal.underlying?.dispose();
    }
    // Remove the window resize event listener since we're now using ResizeObserver
  }

  clearTerminal(): void {
    if (this.terminal && this.terminal.underlying) {
      this.terminal.underlying.clear();
      this.rl.write('$ ');
    }
  }

  async processCommand(command: string, viewOutput: boolean) {
    if (command.trim() === '') {
      return;
    } else if (command === 'clear') {
      this.clearTerminal();
    } else {
      await lastValueFrom(
        this.api.command(command, viewOutput)
      )
        .then((response) => {
          this.rl.write(response);
          // A terminal command can create/modify/delete files in many ways
          // (Rscript, redirects, cp, mkdir, rm, ...), so refresh the explorer
          // tree after any successful command to keep it in sync.
          this.fs.refreshRootNode().catch((err) => console.error('Failed to refresh tree after command:', err));
        })
        .catch((error) => {
          if (error.error && error.error.detail) {
            this.rl.write(error.error.detail);
          } else {
            console.error(error);
          }
        })
        .finally(() => {
          this.rl.write('\n$ ');
        });
    }
  }
}
